/**
 * Ride chart (CHARTS_PLAN §4): elevation behind power / HR / cadence /
 * speed lines, series chips remembered per device, elapsed time or
 * distance on the x axis.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { RideSample } from '../../types/api.ts';
import {
  RIDE_SERIES,
  availableSeries,
  rideRows,
  seriesStats,
  visibleSeries,
  type RideRow,
  type RideSeries,
} from '../../lib/chartData.ts';
import { readStored, useStoredChoice, writeStored } from '../../hooks/useStoredChoice.ts';
import { ChartMessage, Chip, ChipGroup, SeriesSwatch, TooltipBox, TooltipRow } from './ChartParts.tsx';

const SERIES_KEY = 'ride_chart_series';
const X_AXIS_KEY = 'ride_chart_x';
const DEFAULT_SERIES: RideSeries[] = ['power', 'hr'];
type XMode = 'time' | 'distance';
const X_MODES: readonly XMode[] = ['time', 'distance'];

const STYLE: Record<RideSeries, { color: string; dash?: string; unit: string }> = {
  power: { color: 'var(--chart-power)', unit: 'W' },
  hr: { color: 'var(--chart-hr)', dash: '6 3', unit: 'bpm' },
  cadence: { color: 'var(--chart-cadence)', dash: '2 2', unit: 'rpm' },
  speed: { color: 'var(--chart-speed)', dash: '8 3 2 3', unit: 'km/h' },
};

function loadSeries(): RideSeries[] {
  const raw = readStored(SERIES_KEY);
  if (raw === null) return DEFAULT_SERIES;
  return RIDE_SERIES.filter((s) => raw.split(',').includes(s));
}

function formatElapsed(value: number): string {
  // Seconds → h:mm or mm.
  const totalMin = Math.floor(value / 60);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}`;
  return `${m}m`;
}

function formatKm(value: number): string {
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} km`;
}

export function RideChart({
  samples,
  hasSamples,
}: Readonly<{ samples: RideSample[]; hasSamples: boolean }>) {
  const { t } = useTranslation();
  const rows = useMemo(() => rideRows(samples), [samples]);
  if (!hasSamples || rows.length === 0) {
    return <ChartMessage>{t('rideDetail.noChartData')}</ChartMessage>;
  }
  return <RideChartBody rows={rows} />;
}

function RideChartBody({ rows }: Readonly<{ rows: RideRow[] }>) {
  const { t } = useTranslation();
  const [chosen, setChosen] = useState<RideSeries[]>(loadSeries);
  const [xMode, setXMode] = useStoredChoice<XMode>(X_AXIS_KEY, X_MODES, 'time');
  const available = useMemo(() => availableSeries(rows), [rows]);
  const visible = visibleSeries(chosen, available);
  const hasDistance = rows.some((r) => r.km != null);
  const axis: XMode = hasDistance ? xMode : 'time';

  const toggle = (series: RideSeries) => {
    const shown = visible.includes(series);
    if (shown && visible.length === 1) return; // keep one line on the chart
    const next = shown ? chosen.filter((s) => s !== series) : [...chosen, series];
    setChosen(next);
    writeStored(SERIES_KEY, next.join(','));
  };

  return (
    <div className="rounded-lg bg-(--color-bg-secondary) border border-(--color-border) p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-medium text-(--color-text-secondary)">
          {t('rideDetail.chart.title')}
        </h3>
        {hasDistance && (
          <ChipGroup
            label={t('charts.ride.xAxis')}
            value={axis}
            onChange={setXMode}
            options={[
              { value: 'time', label: t('charts.ride.time') },
              { value: 'distance', label: t('charts.ride.distance') },
            ]}
          />
        )}
      </div>
      <fieldset className="flex flex-wrap gap-1.5 border-0 p-0 m-0">
        <legend className="sr-only">{t('charts.ride.series')}</legend>
        {available.map((s) => (
          <Chip key={s} pressed={visible.includes(s)} onClick={() => toggle(s)}>
            <SeriesSwatch color={STYLE[s].color} dash={STYLE[s].dash} />
            {t(`charts.ride.${s}`)}
          </Chip>
        ))}
      </fieldset>
      <RidePlot rows={rows} visible={visible} axis={axis} />
    </div>
  );
}

function rideSummary(rows: RideRow[], visible: RideSeries[], t: TFunction): string {
  const parts = visible.flatMap((s) => {
    const stats = seriesStats(rows, s);
    if (!stats) return [];
    return [t('charts.ride.summaryPart', { name: t(`charts.ride.${s}`), ...stats, unit: STYLE[s].unit })];
  });
  return t('charts.ride.summary', { parts: parts.join('; ') });
}

function RidePlot({
  rows,
  visible,
  axis,
}: Readonly<{ rows: RideRow[]; visible: RideSeries[]; axis: XMode }>) {
  const { t } = useTranslation();
  const data = axis === 'distance' ? rows.filter((r) => r.km != null) : rows;
  const hasAltitude = rows.some((r) => r.altitude != null);
  return (
    <div role="img" aria-label={rideSummary(rows, visible, t)} style={{ width: '100%', height: 260 }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
          <CartesianGrid stroke="var(--chart-grid)" strokeDasharray="3 3" />
          <XAxis
            dataKey={axis === 'distance' ? 'km' : 'elapsed'}
            type="number"
            domain={['dataMin', 'dataMax']}
            tickFormatter={axis === 'distance' ? formatKm : formatElapsed}
            stroke="var(--chart-axis)"
            fontSize={11}
          />
          {hasAltitude && (
            <YAxis yAxisId="alt" hide domain={['dataMin - 10', 'dataMax + 10']} />
          )}
          {visible.map((s, i) => (
            <YAxis
              key={s}
              yAxisId={s}
              hide={i > 1}
              orientation={i === 1 ? 'right' : 'left'}
              stroke={STYLE[s].color}
              fontSize={11}
              width={45}
            />
          ))}
          <Tooltip content={<RideTooltip visible={visible} axis={axis} />} />
          {hasAltitude && (
            <Area
              yAxisId="alt"
              type="monotone"
              dataKey="altitude"
              name={t('charts.ride.elevation')}
              stroke="none"
              fill="var(--chart-elevation)"
              fillOpacity={1}
              connectNulls
              isAnimationActive={false}
            />
          )}
          {visible.map((s) => (
            <Line
              key={s}
              yAxisId={s}
              type="monotone"
              dataKey={s}
              name={t(`charts.ride.${s}`)}
              stroke={STYLE[s].color}
              strokeDasharray={STYLE[s].dash}
              dot={false}
              strokeWidth={1.5}
              connectNulls
              isAnimationActive={false}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function valueText(value: number | null, unit: string): string {
  return value == null ? '—' : `${Math.round(value * 10) / 10} ${unit}`;
}

function RideTooltip({
  active,
  payload,
  visible,
  axis,
}: Readonly<{
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: RideRow }>;
  visible: RideSeries[];
  axis: XMode;
}>) {
  const { t } = useTranslation();
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const title =
    axis === 'distance' && row.km != null
      ? `${row.km.toFixed(2)} km`
      : `${t('rideDetail.chart.elapsed')}: ${formatElapsed(row.elapsed)}`;
  return (
    <TooltipBox title={title}>
      {visible.map((s) => (
        <TooltipRow key={s} label={t(`charts.ride.${s}`)} value={valueText(row[s], STYLE[s].unit)} />
      ))}
      {visible.includes('power') && row.powerMax != null && (
        <TooltipRow label={t('charts.ride.peakPower')} value={`${row.powerMax} W`} />
      )}
      {row.altitude != null && (
        <TooltipRow label={t('charts.ride.elevation')} value={`${Math.round(row.altitude)} m`} />
      )}
    </TooltipBox>
  );
}
